import styled, { css } from 'styled-components';
import { ifProp, ifNotProp } from 'styled-tools';

import { colours } from 'themes/default';

const CarriageWrapper = styled.div`
  position: relative;

  ${ifProp(
    'color',
    css`
      &:after {
        content: '';
        display: block;
        background-color: ${({ color }) => color};
        width: 100%;
        height: 4px;
      }
    `
  )}
`;

const StyledCarriageBlockedTooltip = styled.div`
  display: none;
  color: ${colours.Messaging.ErrorRed};
  position: absolute;
  top: -2em;
  left: 0;
  background: white;
  border: 1px solid lightgrey;
  padding: 0 4px;
  border-radius: 3px;
  white-space: nowrap;
`;

const Carriage = styled('div')`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: clamp(52px, 5vw, 75px);
  justify-content: flex-start;
  background-color: ${colours.Greyscale.White};
  position: relative;

  ${ifProp(
    'selected',
    css`
      /* BRIGHT BLUE BOX AROUND CURRENT CARRIAGE */
      border: 3px solid rgba(16, 159, 179, 0.7);
      padding: 1px 1px 1px 4px;
    `,
    css`
      border: none;
      margin: 0 0 0 3px;
      padding: 4px;
    `
  )}

  ${ifNotProp(
    'sameInventoryClass',
    css`
      & {
        /* CROSS THROUGH CARRIAGE */
        background: linear-gradient(
          to top left,
          ${colours.Greyscale.White} calc(50% - 1px),
          ${colours.Greyscale.CloudGrey},
          ${colours.Greyscale.White} calc(50% + 1px)
        );
      }
    `
  )} 

  &:hover {
    ${ifProp(
      'sameInventoryClass',
      ifNotProp(
        'selected',
        css`
          cursor: pointer;
          /* BRIGHT BLUE FOR SOMETHING? */
          border: 3px solid ${colours.Core.SecondaryPlum.opacity(30)};
          padding: 1px 1px 1px 4px;
          margin: 0;
        `
      )
    )}

    ${StyledCarriageBlockedTooltip} {
      display: block;
    }
  }
`;

const StyledTrainMap = styled('div')`
  display: flex;
  margin: 30px 0;
  /* TRAIN BACKGROUND GREY */
  background-color: ${colours.Greyscale.MistGrey};
  padding: 10px 5px;

  > ${CarriageWrapper} {
    /* Evenly divide the total width by the amount of carriages (less the tail and nose i.e. -2) */
    width: ${({ count }) => `calc(100% / ${count - 2})`};
  }

  .tail {
    border: none;
    position: relative;
    transform: rotate(180deg);
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${({ count }) => `calc(100% / ${count - 2})`};

    /* Creates elliptical tail shape */
    background-image: radial-gradient(ellipse at 0px 50%, #fff 68%, #eee 70%);
  }

  .head {
    position: relative;
    overflow: hidden;
    margin-left: 3px;
    border-right: none;
    display: flex;
    justify-content: center;
    align-items: center;

    /* Creates dome and nose tip effects on front of train */
    background-image: radial-gradient(ellipse at 0px 50%, rgba(6, 24, 61, 0.7) 68%, #eee 70%),
      radial-gradient(circle at 105% 50%, #000 18%, transparent 20%);

    /* Sets head width to 150% of carriages - head needs to be oblong and not rectangle so that we can create elliptical shape */
    width: ${({ count }) => `calc((100% / ${count - 2}) + ((100% / ${count - 2}) / 2) )`};

    /* Creates elliptical nose shape (similar to the tail) */
    :before {
      content: '';
      display: block;
      padding-top: 75%;
      position: absolute;
      background-color: ${colours.Greyscale.White};
      left: -135%;
      width: 200%;
      border-radius: 100%;
    }

    .chevron-right {
      position: absolute;
      z-index: 10;
      transform: translate(-10px, 0);
    }
  }
`;

const StyledCarriageWheelchairIcon = styled.img.attrs(() => ({
  src: '/disabled-16.svg'
}))``;

const StyledCarriageFeatures = styled.div`
  display: flex;
  height: 100%;
  align-items: flex-end;
  justify-content: space-between;
  flex: 1;
`;

const StyledCarriageNumber = styled.span`
  font-size: 1.2em;
  display: flex;
  align-items: flex-end;
  width: 100%;
  justify-content: center;
  flex: 2;
`;

const StyledNumberOfAvailableSeats = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  top: -1em;
  left: calc(50% - 1em);
  background-color: ${colours.Shades.TintBlue10};
  width: 2em;
  border-radius: 50%;
  height: 2em;
  font-size: 0.8em;
`;

const StyledCarriageClass = styled.span`
  font-size: 0.8em;
  line-height: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledAnimals = styled.svg`
  position: absolute;
  right: 4px;
  top: 4px;
  z-index: 1;
  width: 20px;
  height: 25px;
`;

const StyledAnimalsNumber = styled.span`
  position: absolute;
  right: ${(props) => (props.value === 1 ? '8px' : '6px')};
  top: 4px;
  z-index: 1;
  color: ${colours.Messaging.ErrorRed};
  font-size: 13px;
`;

const StyledAssistance = styled.svg`
  position: absolute;
  left: 4px;
  top: ${(props) => (props.value > 9 ? '11px' : '6px')};
  z-index: 1;
  width: 20px;
  height: 25px;
`;

const StyledAssistanceNumber = styled.span`
  position: absolute;
  left: ${(props) => (props.value > 9 ? '4px' : '9px')};
  top: 2px;
  z-index: 1;
  color: ${colours.Core.CoreNavy};
  font-size: 13px;
`;

export {
  StyledTrainMap,
  StyledCarriageWheelchairIcon,
  StyledCarriageFeatures,
  StyledCarriageNumber,
  StyledCarriageBlockedTooltip,
  StyledNumberOfAvailableSeats,
  StyledCarriageClass,
  CarriageWrapper,
  Carriage,
  StyledAnimals,
  StyledAnimalsNumber,
  StyledAssistance,
  StyledAssistanceNumber
};
